import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { User, Hostel } from '../types';
import { authService, hostelService } from '../services/api';

interface AuthContextType {
  user: User | null;
  hostel: Hostel | null;
  /** Changes when active hostel changes — use as React `key` to remount hostel-scoped UI. */
  hostelScopeKey: string;
  loading: boolean;
  login: (phoneNumber: string, password: string) => Promise<void>;
  /** Re-issue JWT for a different hostel (users with multiple `hostelIds`). Not used for super admin. */
  switchHostel: (hostelId: number) => Promise<void>;
  logout: () => void;
  selectHostel: (hostel: Hostel) => void;
  /** Merge tenant self-service profile updates into session. */
  refreshUser: (userData: User) => void;
  isAdmin: () => boolean;
  isSuperAdmin: () => boolean;
  isHostelAdmin: () => boolean;
  isStaff: () => boolean;
  isTenant: () => boolean;
  isGuest: () => boolean;
  /** Tenant or daily guest portal user */
  isResident: () => boolean;
  hasRole: (role: string) => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

function normalizeUserRoles(userData: User): User {
  const raw = userData.roles;
  if (!Array.isArray(raw)) return userData;
  const roles = raw
    .map((r) => {
      if (typeof r === 'string') {
        const n = r.toUpperCase();
        return n.startsWith('ROLE_') ? n : `ROLE_${n}`;
      }
      if (r && typeof r === 'object' && 'name' in r) {
        const n = String((r as { name: string }).name).toUpperCase();
        if (!n) return '';
        return n.startsWith('ROLE_') ? n : `ROLE_${n}`;
      }
      return '';
    })
    .filter(Boolean);
  return { ...userData, roles };
}

function normalizeHostelIds(raw: unknown): number[] {
  if (raw == null) return [];
  let list: unknown[] = [];
  if (Array.isArray(raw)) list = raw;
  else if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) list = parsed;
    } catch {
      return [];
    }
  }
  const out: number[] = [];
  const seen = new Set<number>();
  for (const v of list) {
    const n = Number(v);
    if (Number.isFinite(n) && n > 0 && !seen.has(n)) {
      seen.add(n);
      out.push(n);
    }
  }
  return out;
}

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [hostel, setHostel] = useState<Hostel | null>(null);
  const [hostelScopeKey, setHostelScopeKey] = useState('none');
  const [loading, setLoading] = useState(true);

  const applyHostelScope = (nextHostel: Hostel | null) => {
    setHostel(nextHostel);
    setHostelScopeKey(nextHostel?.id != null ? String(nextHostel.id) : 'none');
  };

  const applySessionUser = (userData: User) => {
    let hostelIds = normalizeHostelIds(userData.hostelIds);
    if (!hostelIds.length && userData.hostelId != null) {
      const hid = Number(userData.hostelId);
      if (Number.isFinite(hid)) hostelIds = [hid];
    }
    const normalized = normalizeUserRoles({
      ...userData,
      hostelIds,
    });
    setUser(normalized);
    localStorage.setItem('user', JSON.stringify(normalized));
    if (normalized.hostel) {
      localStorage.setItem('hostel', JSON.stringify(normalized.hostel));
      applyHostelScope(normalized.hostel as Hostel);
    } else {
      localStorage.removeItem('hostel');
      applyHostelScope(null);
    }
  };

  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    const storedHostel = localStorage.getItem('hostel');
    const token = localStorage.getItem('token');

    const finishLoading = () => setLoading(false);

    if (!storedUser || !token) {
      finishLoading();
      return;
    }

    try {
      const userData = JSON.parse(storedUser) as User;
      setUser(userData);

      if (storedHostel) {
        try {
          const parsed = JSON.parse(storedHostel) as Hostel;
          applyHostelScope(parsed);
        } catch (error) {
          console.error('Error parsing hostel data:', error);
        }
      } else if (userData.hostel) {
        applyHostelScope(userData.hostel as Hostel);
        localStorage.setItem('hostel', JSON.stringify(userData.hostel));
      }

      void authService
        .getCurrentUser()
        .then(({ user: fresh }) => {
          if (!fresh) return;
          const merged: User = {
            ...userData,
            ...fresh,
            hostelIds: normalizeHostelIds(fresh.hostelIds ?? fresh.hostelId ?? userData.hostelIds),
            accessToken: userData.accessToken,
          };
          applySessionUser(merged);
        })
        .catch(() => {
          /* keep cached session */
        })
        .finally(finishLoading);
    } catch (error) {
      console.error('Error parsing user data:', error);
      localStorage.removeItem('user');
      localStorage.removeItem('token');
      localStorage.removeItem('hostel');
      finishLoading();
    }
  }, []);

  /** Login payloads may omit logo/contact — fetch full hostel for branding and invoices. */
  useEffect(() => {
    const id = hostel?.id;
    if (!id) return;

    const hasLogo = Boolean(hostel?.logo?.trim());
    const hasContact = Boolean(
      hostel?.address?.trim() || hostel?.phone?.trim() || hostel?.email?.trim()
    );
    if (hasLogo && hasContact) return;

    let cancelled = false;
    void hostelService
      .getHostelById(id)
      .then((full) => {
        if (cancelled || !full) return;
        setHostel((prev) => {
          if (!prev || prev.id !== id) return prev;
          const merged = { ...prev, ...full };
          localStorage.setItem('hostel', JSON.stringify(merged));
          setHostelScopeKey(String(merged.id));
          return merged;
        });
      })
      .catch(() => {
        /* non-fatal */
      });

    return () => {
      cancelled = true;
    };
  }, [hostel?.id, hostel?.logo, hostel?.address, hostel?.phone, hostel?.email]);

  const login = async (phoneNumber: string, password: string) => {
    const response = await authService.login({ phoneNumber, password });
    const userData = response.user;
    const token = response.accessToken || userData.accessToken;
    if (!token) {
      throw new Error('Login succeeded but no access token returned');
    }

    localStorage.setItem('token', token);
    applySessionUser(userData);
  };

  const switchHostel = async (hostelId: number) => {
    const response = await authService.switchHostel(hostelId);
    const userData = response.user;
    const token = response.accessToken || userData.accessToken;
    if (!token) {
      throw new Error('Switch hostel succeeded but no access token returned');
    }
    localStorage.setItem('token', token);
    applySessionUser(userData);
  };

  const logout = () => {
    localStorage.removeItem('user');
    localStorage.removeItem('token');
    localStorage.removeItem('hostel');
    setUser(null);
    applyHostelScope(null);
  };

  const selectHostel = (selectedHostel: Hostel) => {
    applyHostelScope(selectedHostel);
    localStorage.setItem('hostel', JSON.stringify(selectedHostel));
  };

  const refreshUser = (userData: User) => {
    setUser((prev) => {
      const merged = { ...prev, ...userData, roles: userData.roles ?? prev?.roles };
      localStorage.setItem('user', JSON.stringify(merged));
      return merged;
    });
    if (userData.hostel) {
      applyHostelScope(userData.hostel as Hostel);
      localStorage.setItem('hostel', JSON.stringify(userData.hostel));
    }
  };

  const isSuperAdmin = () => {
    const type = String(user?.userType || '').toUpperCase();
    return (
      type === 'SUPER_ADMIN' ||
      user?.roles?.includes('ROLE_SUPER_ADMIN') === true ||
      user?.roles?.includes('SUPER_ADMIN') === true ||
      false
    );
  };

  const isHostelAdmin = () => {
    return user?.userType === 'HOSTEL_ADMIN' || user?.roles?.includes('ROLE_HOSTEL_ADMIN') || false;
  };

  const isStaff = () => {
    return user?.userType === 'STAFF' || user?.roles?.includes('ROLE_STAFF') || false;
  };

  const isTenant = () => {
    return user?.userType === 'TENANT' || user?.roles?.includes('ROLE_TENANT') || false;
  };

  const isGuest = () => {
    return user?.userType === 'GUEST' || user?.roles?.includes('ROLE_GUEST') || false;
  };

  const isResident = () => isTenant() || isGuest();

  const isAdmin = () => {
    return isSuperAdmin() || isHostelAdmin();
  };

  const hasRole = (role: string) => {
    return user?.roles?.includes(role) || user?.userType === role || false;
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      hostel,
      hostelScopeKey,
      loading, 
      login, 
      switchHostel,
      logout, 
      selectHostel,
      refreshUser,
      isAdmin, 
      isSuperAdmin,
      isHostelAdmin,
      isStaff,
      isTenant,
      isGuest,
      isResident,
      hasRole
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

